import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "../../../../components/ui/form";
import { Button } from "../../../../components/ui/button";
import CreateBrandIcon from "../../../../assets/svgs/common/create-brand.svg";
import { useNavigate } from "react-router-dom";
import { useCreateBrandAdminHttp } from "../../../../hooks/http/admin/brands/create-brand.admin.http";
import { useGetBrandsAdminHttp } from "../../../../hooks/http/admin/brands/get-brand.admin.http";
import useBrandsStore from "../../../../store/brands.store";
import { useToast } from "../../../../hooks/use-toast";
import { FormInput } from "../../../../components/form-components/form-input.components";
import { useCreateAssetCommonHttp } from "../../../../hooks/http/common/utils/asset/create-asset.common.http";
import { useEffect } from "react";
import Spinner from "../../../../components/spinner.components";
import { handleHttpError } from "../../../../utils/utils";

const FormSchema = z.object({
  name: z.string().min(1, { message: "Enter your brand name" }),
  adminEmail: z
    .string()
    .email({ message: "invalid address" })
    .nullable()
    .default(null),
  pan: z
    .array(
      z.object({
        id: z.string(),
        url: z.string(),
      }),
    )
    .default([]),
  panFile: z.array(z.instanceof(File)).default([]),
  gst: z
    .array(
      z.object({
        id: z.string(),
        url: z.string(),
      }),
    )
    .default([]),
  gstFile: z.array(z.instanceof(File)).default([]),
});

export const AddBrandSliderMenuPartial: React.FunctionComponent = () => {
  const { execute: createBrandssHttp, fetching } = useCreateBrandAdminHttp();
  const { execute: getBrandsHttp } = useGetBrandsAdminHttp();
  const { execute: uploadAssetHttp, fetching: uploadingAsset } =
    useCreateAssetCommonHttp();
  const { setData } = useBrandsStore();
  const { toast } = useToast();

  const navigate = useNavigate();
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      pan: [],
      gst: [],
    },
  });

  async function getBrands() {
    const response = await getBrandsHttp({
      body: null,
      queryParams: { page: 1 },
    });

    if (!response.success) {
      return toast({
        content: response.error.message,
        toastType: "error",
      });
    }
    setData(response.data.brands);
  }

  async function uploadAsset(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await uploadAssetHttp({
      body: formData,
    });
    if (!response.success) {
      return false;
    }
    return response.data.asset;
  }

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    try {
      if (data.panFile && data.panFile.length > 0) {
        const uploadedPan = await uploadAsset(data.panFile[0]);
        if (!uploadedPan) {
          return toast({
            content: "Failed to upload PAN file. Please try again",
            toastType: "error",
          });
        }
        form.setValue("pan", [
          {
            id: uploadedPan.id,
            url: uploadedPan.url,
          },
        ]);

        data.pan = [
          {
            id: uploadedPan.id,
            url: uploadedPan.url,
          },
        ];
      }

      if (data.gstFile && data.gstFile.length > 0) {
        const uploadedGst = await uploadAsset(data.gstFile[0]);
        if (!uploadedGst) {
          return toast({
            content: "Failed to upload GST file. Please try again",
            toastType: "error",
          });
        }
        form.setValue("gst", [
          {
            id: uploadedGst.id,
            url: uploadedGst.url,
          },
        ]);

        data.gst = [
          {
            id: uploadedGst.id,
            url: uploadedGst.url,
          },
        ];
      }

      const response = await createBrandssHttp({
        body: {
          name: data.name,
          adminEmail: data.adminEmail,
          pan: data.pan.map((pan) => pan.id),
          gst: data.gst.map((gst) => gst.id),
        },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }
      getBrands();
      navigate("/admin/brands");
    } catch (error) {
      toast({
        content: "Failed to create brand. Please try again",
        toastType: "error",
      });
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];
      if (key) {
        toast({
          content:
            form.formState.errors[key as keyof typeof form.formState.errors]
              ?.message || "",
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors]);

  return (
    <div className="flex flex-col gap-10 w-full h-full">
      <div className="flex items-center gap-5 w-full">
        <CreateBrandIcon />
        <p className="font-semibold text-3xl">Create Brand</p>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col justify-between items-center gap-10 w-full h-full"
        >
          <div className="flex flex-col gap-8 w-full">
            <FormInput
              name="name"
              label="Brand name"
              placeholder="Enter your brand name"
              required
              form={form}
              min={3}
              type="text"
            />
            <FormInput
              name="adminEmail"
              label="Brand admin email"
              placeholder="Enter your brand admin email"
              form={form}
              type="email"
            />
          </div>

          <Button
            disabled={fetching || uploadingAsset}
            type="submit"
            className="flex gap-2 bg-primarybuttonbg py-8 w-full font-semibold text-primarybuttontext text-xl"
          >
            {fetching ? <Spinner width={20} /> : "Invite Brand →"}
          </Button>
        </form>
      </Form>
    </div>
  );
};
